
import { v4 as uuidv4 } from "uuid";
import { 
  Feature,
  IStringNumHash 
} from "@models";
import { 
  Summon,
  AbilityScores,
  HitDice, 
  CharacterFeature,
  DamageMultiplier,
  Character,
  SummonFeature,
  CharacterAbility,
  Creature,
  CreatureHP,
  CharacterResourceHolder,
  CharacterSubscriptionHolder,
  CharacterCondition,
  CreatureAbility
} from "@dndModels";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import { AbilityScoreType, CreatureType, isCreatureType } from "@dndModels/Types";

const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export class SummonInstance {
  true_id = "";
  name = "";
  description = "";
  subscription_holder: CharacterSubscriptionHolder = new CharacterSubscriptionHolder();
  stat_block_id = "";
  creature_id = "";
  connected = false;
  is_new = true;
  base_slot_level = -1;
  slot_level = -1;
  image_url = "";
  // home
  challenge_rating = 0;
  get use_challenge_rating() {
    return this.challenge_rating;
  }
  get creature_type_string() {
    if (this.swarm) {
      return `Swarm of ${this.size} ${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    } else {
      return `${this.creature_type}${this.subtypes.length > 0 ? " (" + this.subtypes.join(" ") + ")" : ""}`;
    }
  }
  xp = 0;
  creature_type: CreatureType = "None"; // Beast, Dragon, Humanoid, etc.
  subtypes: string[] = []; // Drow, Bronze, etc.
  swarm: boolean = false;

  hit_dice_bonus = 0;

  resource_holder: CharacterResourceHolder = new CharacterResourceHolder();
  get hp() {
    return this.resource_holder.hp;
  }
  get hit_dice() {
    return this.resource_holder.hit_dice;
  }
  extra_hp_pools: CreatureHP[] = [];

  initiative_modifier = 0;
  armor_class = 0;
  get use_ac() {
    return this.armor_class;
  }
  size = "";

  get use_size() {
    return this.size;
  }
  alignment = "";
  // attributes
  current_speed: IStringNumHash = {};
  speed: IStringNumHash = {};
  current_senses: IStringNumHash = {};
  senses: IStringNumHash = {};
  passives: {
    passive_perception: number,
    passive_insight: number,
    passive_investigation: number
  } = {
    passive_perception: 0,
    passive_insight: 0,
    passive_investigation: 0
  };

  favorite_conditions: string[] = [];
  // condition_hash: { [id: string]: { level: number, source_true_id: string } } = {};
  condition_immunities: string[] = [];
  conditions: CharacterCondition[] = [];

  get condition_hash() {
    const hash:{ [id: string]: { level: number, source_true_id: string } } = {};
    for (const cc of this.conditions) {
      hash[cc.source_id] = { level: cc.level, source_true_id: cc.condition_source_true_id };
    }
    return hash;
  }

  damage_multipliers: DamageMultiplier[] = [];
  tool_proficiencies: string[] = [];
  // skill_proficiencies: IStringNumHash = {};
  skill_proficiencies2: { skill_id: string, prof_level: number, skill_name: string, modifier: number }[] = [];
  saving_throws: {
    STR: number,
    DEX: number,
    CON: number,
    INT: number,
    WIS: number,
    CHA: number
  } = {
    STR: 0,
    DEX: 0,
    CON: 0,
    INT: 0,
    WIS: 0,
    CHA: 0
  };
  languages = "";
  
  current_ability_scores: AbilityScores = new AbilityScores();
  ability_scores: AbilityScores = new AbilityScores();

  actions: CharacterFeature[] = [];
  legendary_actions: CharacterFeature[] = [];
  lair_actions: CharacterFeature[] = [];
  special_abilities: CharacterFeature[] = [];
  
  abilities: CharacterAbility[] = []; 
  // This is just a holder for special resources used to tie it to the actions
  // Currently not fully implemented for SummonInstance.

  get action_cfs() {
    return this.actions.filter(o => !(o.floating_feature instanceof CreatureAbility) || (o.floating_feature.casting_time !== "BA" && o.floating_feature.casting_time !== "RA"));
  }
  get bonus_actions() {
    return this.actions.filter(o => o.floating_feature instanceof CreatureAbility && o.floating_feature.casting_time === "BA");
  }
  get reactions() {
    return this.actions.filter(o => o.floating_feature instanceof CreatureAbility && o.floating_feature.casting_time === "RA");
  }

  source_id = "";
  source_type = "";
  source_name = "";
  root_id = "";
  root_type = "";
  root_name = "";
  extra_attacks = 0;
  extra_attacks_string = "";

  use_resources: {
    resource_id: string, // Can be "Slot:Level" or "Pact:Level"
    used: number,
    total: number
  }[] = [];

  get proficiency_modifier(): number {
    return common_util.proficiencyBonus(this.challenge_rating);
  }

  get creature(): Creature | null {
    return null;
  }

  get spellcasting_description(): string {
    return "";
  }

  get use_spells(): { spell_level: number, spell_id: string, cast_type: "Slot" | "At Will" | "X/Day" | "Ritual", x: number }[] {
    return [];
  }

  get use_slots2(): {
    resource_id: string, // Can be "Slot:Level" or "Pact:Level"
    used: number,
    total: number
  }[] {
    return [];
  }

  get legendary_actions_per_round(): number {
    return 0;
  }

  get legendary_resistances(): number {
    return 0;
  }

  get used_legendary_resistances() {
    return 0;
  }

  get used_legendary_actions() {
    return 0;
  }

  get_speed(type: string): number {
    const the_speed = (this.speed as any)[type];
    if (the_speed) {
      return the_speed;
    }
    return 0;
  }

  constructor(obj?: any) {
    this.true_id = obj ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.is_new = false;
      this.stat_block_id = obj.stat_block_id ? obj.stat_block_id : "";
      this.creature_id = obj.creature_id ? obj.creature_id : "";
      this.base_slot_level = obj.base_slot_level ? obj.base_slot_level : -1;
      this.slot_level = obj.slot_level ? obj.slot_level : -1;
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      (this.creature_type as string) = obj.creature_type ? obj.creature_type : (obj.type ? obj.type : "");
      if (!isCreatureType(this.creature_type)) {
        this.creature_type = "None";
      }
      this.subtypes = obj.subtypes ? obj.subtypes : [];
      this.swarm = obj.swarm ? obj.swarm : false;
      if (this.subtypes.length === 0 && obj.subtype) {
        const subtype = obj.subtype ? obj.subtype : "";
        if (subtype !== "") {
          this.subtypes.push(subtype);
        }
      }
      this.challenge_rating = obj.challenge_rating ? obj.challenge_rating : 0;
      this.image_url = obj.image_url ? obj.image_url : "";
      this.current_ability_scores = obj.current_ability_scores ? new AbilityScores(obj.current_ability_scores) : new AbilityScores();
      this.ability_scores = obj.ability_scores ? new AbilityScores(obj.ability_scores) : new AbilityScores();
      this.actions = [];
      if (obj.actions && obj.actions.length > 0) {
        obj.actions.forEach((o: any) => {
          if (o.summonFeature) {
            this.actions.push(new SummonFeature(o));
          } else {
            this.actions.push(new CharacterFeature(o));
          }
        });
      }
      this.legendary_actions = [];
      if (obj.legendary_actions && obj.legendary_actions.length > 0) {
        obj.legendary_actions.forEach((o: any) => {
          this.legendary_actions.push(new CharacterFeature(o));
        });
      }
      this.special_abilities = [];
      if (obj.special_abilities && obj.special_abilities.length > 0) {
        obj.special_abilities.forEach((o: any) => {
          this.special_abilities.push(new CharacterFeature(o));
        });
      }

      // console.log(obj);
      if (obj.resource_holder) {
        this.resource_holder = new CharacterResourceHolder(obj.resource_holder);
      } else {
        this.resource_holder = new CharacterResourceHolder();
        this.resource_holder.hp = obj.hp ? new CreatureHP(obj.hp) : new CreatureHP();
        this.resource_holder.hit_dice = []; 
        if (obj.hit_dice) {
          if (obj.hit_dice.length) {
            obj.hit_dice.forEach((hd: any) => {
              this.resource_holder.hit_dice.push(new HitDice(hd));
            });
          } else {
            this.resource_holder.hit_dice.push(new HitDice(obj.hit_dice));
          }
        }
      }
      this.hit_dice_bonus = obj.hit_dice_bonus ? obj.hit_dice_bonus : 0;

      this.current_speed = obj.current_speed ? obj.current_speed : {
        walk: 0,
        swim: 0,
        climb: 0,
        fly: 0,
        burrow: 0,
        hover: 0
      };
      this.speed = obj.speed ? obj.speed : {
        walk: 0,
        swim: 0,
        climb: 0,
        fly: 0,
        burrow: 0,
        hover: 0
      };
      this.passives = obj.passives ? {
        passive_perception: obj.passives.passive_perception ? obj.passives.passive_perception : 0,
        passive_insight: obj.passives.passive_insight ? obj.passives.passive_insight : 0,
        passive_investigation: obj.passives.passive_investigation ? obj.passives.passive_investigation : 0
      } : {
        passive_perception: 0,
        passive_insight: 0,
        passive_investigation: 0
      };
      this.saving_throws = obj.saving_throws ? obj.saving_throws : {
        STR: 0, DEX: 0, CON: 0, 
        INT: 0, WIS: 0, CHA: 0
      };
      this.languages = obj.languages ? obj.languages : "";
      this.tool_proficiencies = obj.tool_proficiencies && obj.tool_proficiencies.length > 0 ? obj.tool_proficiencies : [];
      // this.skill_proficiencies = obj.skill_proficiencies ? obj.skill_proficiencies : {};
      this.skill_proficiencies2 = obj.skill_proficiencies2 ? obj.skill_proficiencies2 : [];
      this.initiative_modifier = obj.initiative_modifier ? obj.initiative_modifier : 0;
      this.armor_class = obj.armor_class ? obj.armor_class : 0;
      this.size = obj.size ? obj.size : "Medium";
      this.alignment = obj.alignment ? obj.alignment : "Neutral";
      this.xp = obj.xp ? obj.xp : 0;
      
      this.favorite_conditions = obj.favorite_conditions ? obj.favorite_conditions : [];
      // this.condition_hash = {};
      // if (obj.condition_hash) {
      //   const cond_ids = Object.keys(obj.condition_hash);
      //   for (const cond_id of cond_ids) {
      //     if (typeof obj.condition_hash[cond_id] === "object") {
      //       this.condition_hash[cond_id] = obj.condition_hash[cond_id];
      //     } else {
      //       this.condition_hash[cond_id] = { level: obj.condition_hash[cond_id], source_true_id: "" };
      //     }
      //   }
      // }
      this.conditions = [];
      if (obj && obj.conditions) {
        for (let i = 0; i < obj.conditions.length; i++) {
          const cc = new CharacterCondition(obj.conditions[i]);
          this.conditions.push(cc);
        }
      }

      this.condition_immunities = obj.condition_immunities ? obj.condition_immunities : [];
      
      this.damage_multipliers = [];
      if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
        obj.damage_multipliers.forEach((dm: any) => {
          this.damage_multipliers.push(new DamageMultiplier(dm));
        });
      }
      this.senses = {};
      if (obj.senses) {
        if (obj.senses.length > 0) {
          obj.senses.forEach((sense: any) => {
            console.log(sense);
            // this.senses[sense.name] = sense.range;
            // this.senses.push(new CharacterSense(sense));
          });
        } else {
          this.senses = obj.senses;
        }
      }
      this.current_senses = {};
      if (obj.current_senses) {
        if (obj.current_senses.length > 0) {
          obj.current_senses.forEach((sense: any) => {
            console.log(sense);
            // this.senses[sense.name] = sense.range;
            // this.current_senses.push(new CharacterSense(sense));
          });
        } else {
          this.current_senses = obj.current_senses;
        }
      }
      this.source_id = obj.source_id ? obj.source_id : "";
      this.source_name = obj.source_name ? obj.source_name : "";
      this.source_type = obj.source_type ? obj.source_type : "";
      this.root_id = obj.root_id ? obj.root_id : "";
      this.root_name = obj.root_name ? obj.root_name : "";
      this.root_type = obj.root_type ? obj.root_type : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      stat_block_id: this.stat_block_id,
      creature_id: this.creature_id,
      base_slot_level: this.base_slot_level,
      slot_level: this.slot_level,
      instance_type: "Summon",
      name: this.name,
      description: this.description,
      creature_type: this.creature_type,
      subtypes: this.subtypes,
      swarm: this.swarm,
      image_url: this.image_url,
      current_ability_scores: this.current_ability_scores.toDBObj(),
      ability_scores: this.ability_scores.toDBObj(),
      hit_dice_bonus: this.hit_dice_bonus,
      actions: this.actions.map(o => o.toDBObj()),
      legendary_actions: this.legendary_actions.map(o => o.toDBObj()),
      special_abilities: this.special_abilities.map(o => o.toDBObj()),
      challenge_rating: this.challenge_rating,
      resource_holder: this.resource_holder.toDBObj(),
      current_speed: this.current_speed,
      speed: this.speed,
      passives: this.passives,
      saving_throws: this.saving_throws,
      // skill_proficiencies: this.skill_proficiencies,
      skill_proficiencies2: this.skill_proficiencies2,
      tool_proficiencies: this.tool_proficiencies,
      initiative_modifier: this.initiative_modifier,
      armor_class: this.armor_class,
      size: this.size,
      alignment: this.alignment,
      xp: this.xp,
      favorite_conditions: this.favorite_conditions,
      // condition_hash: this.condition_hash,
      conditions: this.conditions.map(o => o.toDBObj()),
      condition_immunities: this.condition_immunities,
      damage_multipliers: this.damage_multipliers,
      languages: this.languages,
      source_id: this.source_id,
      source_name: this.source_name,
      source_type: this.source_type,
      root_id: this.root_id,
      root_name: this.root_name,
      root_type: this.root_type
    };
  }

  get_use_name(): string {
    return this.name;
  }

  get hit_dice_string(): string {
    let response = "";
    let hd_bonus = 0;
    for (const hd of this.hit_dice) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `${hd.count}d${hd.size}`;
      hd_bonus += hd.count * this.hit_dice_bonus;
    }
    if (hd_bonus !== 0) {
      response += ` ${common_util.addPlusMaybe(hd_bonus)}`;
    }
    return response;
  }

  // get skills_string(): string {
  //   let response = "";
  //   Object.keys(this.skill_proficiencies).forEach(key => {
  //     if (response.length > 0) {
  //       response += ", ";
  //     }
  //     response += `${key}: ${this.skill_proficiencies[key]}`;
  //   });
  //   return response;
  // }

  get skills_string(): string {
    return this.skill_proficiencies2.map(o => { return `${o.skill_name}: ${o.modifier < 0 ? o.modifier : "+" + o.modifier}`; }).join(", ");
  }

  get tools_string(): string {
    return this.tool_proficiencies.join(", ");
  }

  get senses_string(): string {
    let response = "";
    Object.keys(this.current_senses).forEach(key => {
      if (response.length > 0) {
        response += ", ";
      }
      response += `${key} ${this.current_senses[key]} ft.`;
    });
    // if (this.passives.passive_perception > 0) {
    //   if (response.length > 0) {
    //     response += ", ";
    //   }
    //   response += `Passive Perception: ${this.passives.passive_perception}`;
    // }
    // if (this.passives.passive_insight > 0) {
    //   if (response.length > 0) {
    //     response += ", ";
    //   }
    //   response += `Passive Insight: ${this.passives.passive_insight}`;
    // }
    // if (this.passives.passive_investigation > 0) {
    //   if (response.length > 0) {
    //     response += ", ";
    //   }
    //   response += `Passive Investigation: ${this.passives.passive_investigation}`;
    // }
    return response;
  }
  get passive_perception() {
    let passive_perception = 10 + this.current_ability_scores.WIS;
    const perception = this.skill_proficiencies2.find(o => o.skill_id === "5fef742e7494ba20cca8a9ec" || o.skill_id === "Perception");
    if (perception) {
      passive_perception += this.proficiency_modifier * perception.prof_level;
    }
    return passive_perception;
  }
  get small_name() {
    return this.creature ? this.creature.small_name : this.name;
  }

  get saving_throws_string(): string {
    let response = "";
    if (this.saving_throws.STR !== 0) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `STR: ${this.saving_throws.STR}`;
    }
    if (this.saving_throws.DEX !== 0) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `DEX: ${this.saving_throws.DEX}`;
    }
    if (this.saving_throws.CON !== 0) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `CON: ${this.saving_throws.CON}`;
    }
    if (this.saving_throws.INT !== 0) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `INT: ${this.saving_throws.INT}`;
    }
    if (this.saving_throws.WIS !== 0) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `WIS: ${this.saving_throws.WIS}`;
    }
    if (this.saving_throws.CHA !== 0) {
      if (response.length > 0) {
        response += ", ";
      }
      response += `CHA: ${this.saving_throws.CHA}`;
    }
    return response;
  }

  get_saving_throw(ability: AbilityScoreType) {
    let modifier: number | null | undefined = (this.saving_throws as any)[ability];
    if (!modifier) {
      modifier = this.current_ability_scores.getModifier(ability);
    }
    if (!modifier) {
      return 0
    } else {
      return modifier;
    }
  }
  saving_throw(ability: AbilityScoreType) {
    return this.get_saving_throw(ability);
  }

  get speed_string(): string {
    let response = "";
    Object.keys(this.current_speed).forEach(key => {
      if (this.current_speed[key]) {
        if (response.length > 0) {
          response += ", ";
        }
        if (key === "hover") {
          if (this.current_speed[key] === 1) {
            response += "hover";
          } else {
            response += "no hover";
          }
        } else {
          response += `${key} ${this.current_speed[key]} ft.`;
        }
      }
    });
    return response;
  }

  get immunities_string(): string {
    let return_me = "";
    this.damage_multipliers.filter(o => o.multiplier === 0).forEach(dm => {
      dm.damage_types.forEach(dt => {
        if (return_me.length > 0) {
          return_me += ", ";
        }
        return_me += dt;
      });
    });
    return return_me;
  }

  get condition_immunities_string(): string {
    return this.condition_immunities.join(", ");
  }

  get resistances_string(): string {
    let return_me = "";
    this.damage_multipliers.filter(o => o.multiplier === 0.5).forEach(dm => {
      dm.damage_types.forEach(dt => {
        if (return_me.length > 0) {
          return_me += ", ";
        }
        return_me += dt;
      });
    });
    return return_me;
  }

  get vulnerabilities_string(): string {
    let return_me = "";
    this.damage_multipliers.filter(o => o.multiplier === 2).forEach(dm => {
      dm.damage_types.forEach(dt => {
        if (return_me.length > 0) {
          return_me += ", ";
        }
        return_me += dt;
      });
    });
    return return_me;
  }

  get true_max_hit_points() {
    return this.hp.true_max;
  }

  clone(): SummonInstance {
    return new SummonInstance(this);
  }

  copy(copyMe: SummonInstance): void {
    // this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.creature_type = copyMe.creature_type;
    this.subtypes = [...copyMe.subtypes];
    this.swarm = copyMe.swarm;
    this.current_ability_scores = new AbilityScores(copyMe.current_ability_scores);
    this.ability_scores = new AbilityScores(copyMe.ability_scores);
    this.image_url = copyMe.image_url;
    this.challenge_rating = copyMe.challenge_rating;

    this.resource_holder = copyMe.resource_holder.clone();
    this.hit_dice_bonus = copyMe.hit_dice_bonus;

    this.current_speed = {...copyMe.current_speed};
    this.speed = {...copyMe.speed};
    this.passives = {...copyMe.passives};
    this.saving_throws = {...copyMe.saving_throws};
    // this.skill_proficiencies = {...copyMe.skill_proficiencies};
    this.skill_proficiencies2 = copyMe.skill_proficiencies2.map(o => { return { ...o }; });
    this.tool_proficiencies = {...copyMe.tool_proficiencies};
    this.initiative_modifier = copyMe.initiative_modifier;
    this.armor_class = copyMe.armor_class;
    this.actions = [];
    if (copyMe.actions && copyMe.actions.length > 0) {
      copyMe.actions.forEach((o: any) => {
        this.actions.push(new CharacterFeature(o));
      });
    }
    this.legendary_actions = [];
    if (copyMe.legendary_actions && copyMe.legendary_actions.length > 0) {
      copyMe.legendary_actions.forEach((o: any) => {
        this.legendary_actions.push(new CharacterFeature(o));
      });
    }
    this.special_abilities = [];
    if (copyMe.special_abilities && copyMe.special_abilities.length > 0) {
      copyMe.special_abilities.forEach((o: any) => {
        this.special_abilities.push(new CharacterFeature(o));
      });
    }
    this.size = copyMe.size;
    this.alignment = copyMe.alignment;
    this.xp = copyMe.xp;
    
    this.favorite_conditions = [...copyMe.favorite_conditions];
    // this.condition_hash = {...copyMe.condition_hash};
    this.conditions = copyMe.conditions.map(o => o.clone());
    this.condition_immunities = [...copyMe.condition_immunities];

    this.damage_multipliers = [];
    if (copyMe.damage_multipliers && copyMe.damage_multipliers.length > 0) {
      copyMe.damage_multipliers.forEach((dm: any) => {
        this.damage_multipliers.push(new DamageMultiplier(dm));
      });
    }
    this.languages = copyMe.languages;
    this.senses = { ...copyMe.senses };
    this.current_senses = { ...copyMe.senses };
  }

  copySummon(copyMe: Summon, 
    char: Character, 
    source_id: string, 
    item_id: string,
    base_slot_level: number, 
    slot_level: number
    ): void {
    if (this.is_new) {
      this.source_id = source_id;
      this.name = copyMe.name;
      this.description = copyMe.description;
      this.stat_block_id = copyMe.true_id;
      this.base_slot_level = base_slot_level;
      this.slot_level = slot_level;
      this.creature_type = copyMe.creature_type;
      this.subtypes = [...copyMe.subtypes];
      this.swarm = copyMe.swarm;
      this.current_ability_scores = char_util.toAbilityScores(copyMe.ability_scores, { obj: char, source_id, base_slot_level, slot_level });
      this.ability_scores = this.current_ability_scores.clone();
      this.image_url = copyMe.image_url;
      this.resource_holder.hit_dice = [];
      const hd = new HitDice();
      hd.size = char_util.value(copyMe.hit_dice_size, { obj: char, source_id, item_id, base_slot_level, slot_level });
      hd.count = char_util.value(copyMe.hit_dice_count, { obj: char, source_id, item_id, base_slot_level, slot_level });
      this.hit_dice.push(hd);
      this.challenge_rating = char_util.value(copyMe.challenge_rating, { obj: char, source_id, item_id, base_slot_level, slot_level });
      
      // this.current_hit_points = char_util.value(copyMe.max_hit_points, { obj: char, source_id, item_id, base_slot_level, slot_level });
      // this.max_hit_points = char_util.value(copyMe.max_hit_points, { obj: char, source_id, item_id, base_slot_level, slot_level });
      // this.temp_hit_points = char_util.value(copyMe.temp_hit_points, { obj: char, source_id, item_id, base_slot_level, slot_level });
      this.hp.max = char_util.value(copyMe.max_hit_points, { obj: char, source_id, item_id, base_slot_level, slot_level });
      this.hp.current = this.hp.max;
      this.hp.temp = char_util.value(copyMe.temp_hit_points, { obj: char, source_id, item_id, base_slot_level, slot_level });

      this.current_speed = {...copyMe.speed};
      this.speed = {...copyMe.speed};
      this.passives = {
        passive_perception: char_util.value(copyMe.passives.passive_perception, { obj: char, source_id, item_id, base_slot_level, slot_level }),
        passive_insight: char_util.value(copyMe.passives.passive_insight, { obj: char, source_id, item_id, base_slot_level, slot_level }),
        passive_investigation: char_util.value(copyMe.passives.passive_investigation, { obj: char, source_id, item_id, base_slot_level, slot_level })
      };
      this.saving_throws = {
        STR: char_util.value(copyMe.saving_throws.STR, { obj: char, source_id, item_id, base_slot_level, slot_level }),
        DEX: char_util.value(copyMe.saving_throws.DEX, { obj: char, source_id, item_id, base_slot_level, slot_level }),
        CON: char_util.value(copyMe.saving_throws.CON, { obj: char, source_id, item_id, base_slot_level, slot_level }),
        INT: char_util.value(copyMe.saving_throws.INT, { obj: char, source_id, item_id, base_slot_level, slot_level }),
        WIS: char_util.value(copyMe.saving_throws.WIS, { obj: char, source_id, item_id, base_slot_level, slot_level }),
        CHA: char_util.value(copyMe.saving_throws.CHA, { obj: char, source_id, item_id, base_slot_level, slot_level }),
      };
      // this.skill_proficiencies = {}; 
      // copyMe.skill_proficiencies_upgradable.forEach(upg => {
      //   this.skill_proficiencies[upg.name] = char_util.value(upg.value, { obj: char, source_id, item_id, base_slot_level, slot_level });
      // });
      this.skill_proficiencies2 = []; 
      for (const upg of copyMe.skill_proficiencies_upgradable) {
        const modifier =  char_util.value(upg.value, { obj: char, source_id, item_id, base_slot_level, slot_level });
        const skill_id = upg._id;
        const skill_name = upg.name;
        this.skill_proficiencies2.push({ skill_id, skill_name, modifier, prof_level: 1 });
      }
      this.tool_proficiencies = [...copyMe.tool_proficiencies];
      this.initiative_modifier = char_util.value(copyMe.initiative_modifier, { obj: char, source_id, item_id, base_slot_level, slot_level });
      this.armor_class = char_util.value(copyMe.armor_class, { obj: char, source_id, item_id, base_slot_level, slot_level });
      this.size = copyMe.size;
      this.alignment = copyMe.alignment;
      
      this.favorite_conditions = [];
      // this.condition_hash = {};
      this.conditions = [];
      this.condition_immunities = [...copyMe.condition_immunities];
      
      this.damage_multipliers = [];
      if (copyMe.damage_multipliers && copyMe.damage_multipliers.length > 0) {
        copyMe.damage_multipliers.forEach((dm: any) => {
          this.damage_multipliers.push(new DamageMultiplier(dm));
        });
      }
      this.languages = copyMe.languages;
      this.senses = { ...copyMe.senses };
      this.current_senses = { ...copyMe.senses };
    }
    this.actions = [];
    if (copyMe.actions && copyMe.actions.length > 0) {
      copyMe.actions.forEach((o: Feature) => {
        const char_feat = new SummonFeature();
        char_feat.copyFeature(o, null, this);
        if (char_feat.feature) {
          // if (o.feature_type === "Minion Ability") {
          //   char_feat.
          // } else if (o.feature_type === "Minion Extra Attacks") {
          //   char_feat.feature_type = "Extra Attacks";
          //   char_feat.num = (o.floating_feature as UpgradableNumberFeature).num.value({ char, source_id, base_slot_level, slot_level });
          // }
        }
        this.actions.push(char_feat);
      });
    }
    this.legendary_actions = [];
    if (copyMe.legendary_actions && copyMe.legendary_actions.length > 0) {
      copyMe.legendary_actions.forEach((o: any) => {
        const char_feat = new CharacterFeature();
        char_feat.copyFeature(o, null, this);
        this.legendary_actions.push(char_feat);
      });
    }
    this.special_abilities = [];
    if (copyMe.special_abilities && copyMe.special_abilities.length > 0) {
      copyMe.special_abilities.forEach((o: any) => {
        const char_feat = new CharacterFeature();
        char_feat.copyFeature(o, null, this);
        this.special_abilities.push(char_feat);
      });
    }
    this.is_new = false;
    this.connected = true;
  }
}